import axios from 'axios';
import API_URLS from '../config/apiConfig';
import showToast from '../utils/toastUtils';

// Create axios instance without baseURL since we're using full URLs in services
const api = axios.create({});

// Refresh token handling state
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;
let requestQueue: Array<(token: string | null) => void> = [];

const processQueue = (token: string | null) => {
  requestQueue.forEach((callback) => callback(token));
  requestQueue = [];
};

const setTokensFromResponse = (data: any) => {
  try {
    const accessToken: string | undefined = data?.access_token;
    const refreshToken: string | undefined = data?.refresh_token;
    const expiresAtIso: string | undefined = data?.expires_at;
    let expiresAtMs: number | undefined;
    
    if (expiresAtIso) {
      expiresAtMs = new Date(expiresAtIso).getTime();
    }
    
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
    }
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    if (expiresAtMs) {
      localStorage.setItem('tokenExpiresAt', String(expiresAtMs));
    }
  } catch (e) {
    // noop
  }
};

const performRefresh = async (): Promise<boolean> => {
  const storedRefreshToken = localStorage.getItem('refreshToken');
  if (!storedRefreshToken) {
    return false;
  }
  
  try {
    const response = await axios.post(
      API_URLS.REFRESH_TOKEN,
      { refresh_token: storedRefreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );
    
    const data = response.data || {};
    setTokensFromResponse(data);
    return true;
  } catch (e: any) {
    // If refresh token API returns 401, we need to logout the user
    if (e.response?.status === 401) {
      console.log('🔐 Refresh token expired/invalid (401), logging out user and redirecting to login');
      // Clear all auth data
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tokenExpiresAt');
      localStorage.removeItem('user');
      localStorage.removeItem('userDetails');
      
      // Trigger a custom event that AuthContext can listen to
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
    return false;
  }
};

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const getCustomErrorMessage = (status: number, defaultMessage?: string): string => {
  if (defaultMessage) {
    return defaultMessage;
  }
  switch (status) {
    case 400: return 'Bad Request: The request was invalid or cannot be processed';
    case 401: return 'Unauthorized: Please log in again to continue';
    case 403: return 'Forbidden: You do not have permission to perform this action';
    case 404: return 'Not Found: The requested resource was not found';
    case 500: return 'Internal Server Error: Something went wrong on our end';
    case 502: return 'Bad Gateway: The server is temporarily unavailable';
    case 503: return 'Service Unavailable: The service is temporarily down';
    default: return `Request failed with status ${status}`;
  }
};

// Helper function to show appropriate toast based on error
const showErrorToast = (error: any, originalRequest: any) => {
  const status = error.response?.status;
  const errorData = error.response?.data || {};
  const isCorsError = !error.response && error.message?.includes('Network Error');
  
  // Don't show toast for logo-image requests
  if (originalRequest.url?.includes('logo-image')) {
    return;
  }
  
  // Don't show toast for 401 errors as they're handled by auth flow
  if (status === 401) {
    return;
  }
  
  // Don't show toast for 404 errors
  if (status === 404) {
    return;
  }
  
  // Don't show toast for retry attempts
  if (originalRequest._retry) {
    return;
  }
  
  // Don't show toast for auth-related endpoints to avoid spam
  const authEndpoints = ['/auth/request-otp', '/auth/verify-otp', '/auth/refresh'];
  const isAuthEndpoint = authEndpoints.some(endpoint => originalRequest.url?.includes(endpoint));
  if (isAuthEndpoint) {
    return;
  }
  
  let message = '';
  let toastType: 'error' | 'warning' = 'error';
  
  if (isCorsError || !error.response) {
    message = 'Network error: Unable to connect to the server. Please check your internet connection.';
  } else {
    message = errorData.error || errorData.detail || errorData.message || getCustomErrorMessage(status);
    
    // Use warning for client errors (4xx), error for server errors (5xx)
    if (status >= 400 && status < 500) {
      toastType = 'warning';
    }
  }
  
  // Show the toast
  if (toastType === 'warning') {
    showToast.warning(message);
  } else {
    showToast.error(message);
  }
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log('Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor with refresh/queue handling
api.interceptors.response.use(
  (response) => {
    console.log('Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const isCorsError = !error.response && error.message?.includes('Network Error');
    const is401Error = status === 401;
    
    console.error('Response Error:', status, error.response);

    // Show error toast for non-401 errors
    showErrorToast(error, originalRequest);

    // Handle both 401 errors and CORS errors that might be 401-related
    if ((is401Error || isCorsError) && !originalRequest._retry) {
      originalRequest._retry = true;

      const existingRefreshToken = localStorage.getItem('refreshToken');
      
      if (!existingRefreshToken) {
        // No refresh token available; perform logout-like cleanup
        console.log('🔐 No refresh token available, logging out user and redirecting to login');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tokenExpiresAt');
        localStorage.removeItem('user');
        localStorage.removeItem('userDetails');
        
        // Trigger logout event
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(error);
      }

      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = performRefresh().finally(() => {
          isRefreshing = false;
        });
      }

      return new Promise((resolve, reject) => {
        requestQueue.push((newToken) => {
          if (newToken) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          } else {
            reject(error);
          }
        });

        refreshPromise
          ?.then((ok) => {
            const updatedToken = ok ? localStorage.getItem('accessToken') : null;
            processQueue(updatedToken);
          })
          .catch(() => {
            processQueue(null);
          });
      });
    }

    return Promise.reject(error);
  }
);

// Thin helpers on top of the shared client
export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: any;
  requiresAuth?: boolean; // if true and no token, immediately throw 401
}

const ensureAuthOrThrow = (requiresAuth?: boolean) => {
  if (requiresAuth) {
    const token = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    if (!token && !refreshToken) {
      throw new ApiError(getCustomErrorMessage(401, 'Authentication required'), 401);
    }
  }
};

export const apiRequest = async (url: string, options: ApiRequestOptions = {}) => {
  const { method = 'GET', headers = {}, body, requiresAuth = true } = options;
  ensureAuthOrThrow(requiresAuth);
  try {
    const response = await api({ method, url, headers, data: body });
    return response.data;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      const errorData = error.response.data || {};
      throw new ApiError(
        errorData.error || errorData.detail || getCustomErrorMessage(status, errorData.message),
        status,
        errorData
      );
    } else if (error.request) {
      throw new ApiError('Network error: Unable to connect to the server. Please check your internet connection.', 0);
    } else {
      throw new ApiError(error.message || 'An unexpected error occurred', 0);
    }
  }
};

export const apiGet = (url: string, options?: Omit<ApiRequestOptions, 'method'>) =>
  apiRequest(url, { ...options, method: 'GET' });

export const apiPost = (url: string, body?: any, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
  apiRequest(url, { ...options, method: 'POST', body });

export const apiPut = (url: string, body?: any, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
  apiRequest(url, { ...options, method: 'PUT', body });

export const apiPatch = (url: string, body?: any, options?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
  apiRequest(url, { ...options, method: 'PATCH', body });



export const apiDelete = (url: string, options?: Omit<ApiRequestOptions, 'method'>) =>
  apiRequest(url, { ...options, method: 'DELETE' });

export default api; 
